import React from 'react';
import { DashboardCard } from '../components/DashboardCard';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Skeleton,
  Box,
} from '@mui/material';
import { useFafsaData } from '../contexts';
import { FafsaFilterControls } from './FafsaFilterControls';

const FafsaTableContent = () => {
  const { data, loading, error, filter, totalsByYear, grandTotal } = useFafsaData();

  if (loading) {
    return <Skeleton variant="rounded" height={300} />;
  }

  const years = [...new Set(data.map(d => d.year))].sort();

  const renderTableContent = () => {
    if (!filter.groupBySchool) {
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>School Year</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Submitted</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Enrollment</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Submission Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {years.map(year => {
                const yearData = data.find(d => d.year === year);
                if (!yearData) return null;
                
                return (
                  <TableRow key={year}>
                    <TableCell>{`${year}-${year + 1}`}</TableCell>
                    <TableCell align="right">{yearData.submittedCount}</TableCell>
                    <TableCell align="right">{yearData.totalEnrollment}</TableCell>
                    <TableCell align="right">
                      <strong>{yearData.percentageSubmitted.toFixed(1)}%</strong>
                    </TableCell>
                  </TableRow>
                );
              })}
              {years.length > 1 && grandTotal && (
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotal.totalSubmitted}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotal.totalEnrollment}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{grandTotal.totalPercentage}%</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      const schools = [...new Set(data.map(d => d.schoolName))].sort();
      
      return (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>School</TableCell>
                {years.map(year => (
                  <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                    {`${year}-${year + 1}`}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {schools.map(school => (
                <TableRow key={school}>
                  <TableCell>{school}</TableCell>
                  {years.map(year => {
                    const cellData = data.find(d => d.year === year && d.schoolName === school);
                    return (
                      <TableCell key={year} align="center">
                        {cellData ? `${cellData.submittedCount} (${cellData.percentageSubmitted.toFixed(1)}%)` : '-'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                {years.map(year => {
                  const yearTotal = totalsByYear.find(t => t.year === year);
                  return (
                    <TableCell key={year} align="center" sx={{ fontWeight: 'bold' }}>
                      {yearTotal ? `${yearTotal.totalSubmitted} (${yearTotal.totalPercentage}%)` : '-'}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }
  };

  return (
    <Box sx={{ height: '100%' }}>
      <FafsaFilterControls />

      {error ? (
        <Typography color="error" align="center">
          {error}
        </Typography>
      ) : !data || data.length === 0 ? (
        <Typography align="center" color="textSecondary">
          No FAFSA data available for selected years
        </Typography>
      ) : (
        <>
          {renderTableContent()}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Note: Counts and submission rates are calculated using 12th grade students only
          </Typography>
        </>
      )}
    </Box>
  );
};

// Helper function to build CSV export
const buildFafsaCsv = (data: any[], filter: any, totalsByYear: any[], grandTotal: any) => {
  const yearsLabel = filter.schoolYears.length > 0
    ? filter.schoolYears.map((y: number) => `${y}-${y + 1}`).join(', ')
    : 'All Years';
  const exportDate = new Date().toLocaleDateString();
  
  const metadata = [
    '# FAFSA Submission Rates',
    `# Years: ${yearsLabel}${filter.groupBySchool ? ' (Grouped by School)' : ''}`,
    `# Exported: ${exportDate}`,
    '',
  ];

  const years = [...new Set(data.map(d => d.year))].sort();

  if (!filter.groupBySchool) {
    // Simple view
    const headers = 'School Year,Submitted,Total Enrollment,Submission Rate';
    const rows = years.map((year: number) => {
      const yearData = data.find(d => d.year === year);
      return `${year}-${year + 1},${yearData.submittedCount},${yearData.totalEnrollment},${yearData.percentageSubmitted.toFixed(1)}%`;
    });
    
    if (years.length > 1 && grandTotal) {
      rows.push(`Total,${grandTotal.totalSubmitted},${grandTotal.totalEnrollment},${grandTotal.totalPercentage}%`);
    }
    
    return [...metadata, headers, ...rows].join('\n');
  } else {
    // Grouped by school
    const schools = [...new Set(data.map(d => d.schoolName))].sort();
    const headers = ['School', ...years.map((y: number) => `${y}-${y + 1}`)].join(',');
    
    const rows = schools.map((school: string) => {
      const cells = years.map((year: number) => {
        const cellData = data.find(d => d.year === year && d.schoolName === school);
        return cellData ? `"${cellData.submittedCount} (${cellData.percentageSubmitted.toFixed(1)}%)"` : '-';
      });
      return [`"${school}"`, ...cells].join(',');
    });
    
    const totalsRow = ['Total', ...years.map((year: number) => {
      const yearTotal = totalsByYear.find(t => t.year === year);
      return yearTotal ? `"${yearTotal.totalSubmitted} (${yearTotal.totalPercentage}%)"` : '-';
    })].join(',');
    
    return [...metadata, headers, ...rows, totalsRow].join('\n');
  }
};

export const FafsaTableCard = () => {
  const { data, filter, totalsByYear, grandTotal } = useFafsaData();

  const handleExport = () => {
    if (!data || data.length === 0) return;

    const csvContent = buildFafsaCsv(data, filter, totalsByYear, grandTotal);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const yearsFilename = filter.schoolYears.length > 0 ? filter.schoolYears.join('_') : 'AllYears';
    const groupSuffix = filter.groupBySchool ? '_BySchool' : '';
    link.setAttribute('download', `FAFSA_${yearsFilename}${groupSuffix}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardCard title="FAFSA Submission" onExport={handleExport}>
      <FafsaTableContent />
    </DashboardCard>
  );
};